import * as React from 'react'
import { Bolt, CircleHelp, LogOut, Menu, Moon, Search, Sun, User, X } from 'lucide-react'
import { useT, useVisitOne } from '@/components/visitone/visitone-provider'
import { useShellLayout } from '@/hooks/use-shell-layout'
import { PrimaryNav } from '@/components/visitone/primary-nav'
import { CommandPalette, type PaletteFeature, type PaletteRoute } from '@/components/visitone/command-palette'
import { ProductMark } from '@/components/visitone/product-mark'
import { IconButton } from '@/components/ui/icon-button'
import type { DestinationId } from '@/components/visitone/navigation-model'

/*
 * AppShell: fixed top bar and left rail around one scrolling content region.
 * The content region is the only scroll container. The palette is available at
 * every breakpoint. The rail expands, collapses to icons, or becomes a drawer.
 * The palette offers the caller's permitted `destinations` (VIS-329). The
 * rail lists `railDestinations`, which is those destinations plus Setup when
 * Landlords is held without the setup destination.
 */
export type ShellAccount = {
  name?: string
  email?: string
  onLogout: () => void
}

export function AppShell({
  current,
  destinations,
  railDestinations = destinations,
  onNavigate,
  onOpenRoute,
  paletteRoutes,
  paletteFeatures,
  hasFeature,
  account,
  tenantSlot,
  languageSlot,
  navigationInert = false,
  children,
}: {
  /** Null on a refused or unknown route: no rail item is marked. */
  current: DestinationId | null
  /** Permitted destinations, from the caller's access in the current tenant. */
  destinations: DestinationId[]
  /** Rail entries. Defaults to `destinations`. */
  railDestinations?: DestinationId[]
  onNavigate?: (id: DestinationId) => void
  /** Opens an in-app route below a destination, e.g. a Setup screen from a favourite or the palette. */
  onOpenRoute?: (route: string, state?: unknown) => void
  paletteRoutes?: PaletteRoute[]
  /** Plan-limited features the caller holds; anything the plan excludes is not passed. */
  paletteFeatures?: PaletteFeature[]
  hasFeature?: (featureId: string) => boolean
  account?: ShellAccount
  tenantSlot?: React.ReactNode
  languageSlot?: React.ReactNode
  /** Rail, drawer button, palette trigger and Cmd/Ctrl+K are unavailable, e.g. during a tenant switch. */
  navigationInert?: boolean
  children: React.ReactNode
}) {
  const t = useT()
  const { appearance, toggleAppearance, density, setDensity } = useVisitOne()
  const layout = useShellLayout()
  const [drawerOpen, setDrawerOpen] = React.useState(false)
  const [paletteOpen, setPaletteOpen] = React.useState(false)

  const isDrawer = layout === 'drawer'
  const isCollapsed = layout === 'collapsed'

  // Command palette opens with Cmd/Ctrl+K anywhere.
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (navigationInert) return
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setPaletteOpen(true)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [navigationInert])

  React.useEffect(() => {
    if (!isDrawer) setDrawerOpen(false)
  }, [isDrawer])

  const handleNavigate = (id: DestinationId) => {
    onNavigate?.(id)
    setDrawerOpen(false)
  }

  const handleOpenRoute = (route: string, state?: unknown) => {
    onOpenRoute?.(route, state)
    setDrawerOpen(false)
  }

  // Density and Help: in the top bar from 900px; below it, in the drawer, or in the tenant strip while the drawer cannot open.
  const preferenceControls = (
    <>
      <IconButton
        label={density === 'comfortable' ? 'Use compact density' : 'Use comfortable density'}
        onClick={() => setDensity(density === 'comfortable' ? 'compact' : 'comfortable')}
      >
        <span aria-hidden="true" className="text-xs font-semibold">
          {density === 'comfortable' ? 'C' : 'c'}
        </span>
      </IconButton>
      <IconButton label={t('core.nav.help')}>
        <CircleHelp />
      </IconButton>
    </>
  )
  const showStrip = isDrawer && (Boolean(tenantSlot) || navigationInert)

  return (
    <div className="flex h-dvh flex-col bg-page">
      <header
        className="flex min-h-14 shrink-0 items-center gap-2 border-b border-line bg-surface px-3 sm:px-4"
        style={{ minHeight: 'var(--vo-topbar-height)' }}
      >
        {isDrawer ? (
          <IconButton
            label={t('core.nav.openNavigation')}
            aria-haspopup="dialog"
            disabled={navigationInert}
            onClick={() => setDrawerOpen(true)}
          >
            <Menu />
          </IconButton>
        ) : null}

        <ProductMark showName={!isDrawer} />

        {!isDrawer && tenantSlot ? <div className="ml-1 min-w-0">{tenantSlot}</div> : null}

        <button
          type="button"
          disabled={navigationInert}
          onClick={() => setPaletteOpen(true)}
          aria-haspopup="dialog"
          aria-keyshortcuts="Control+K Meta+K"
          className="mx-auto flex h-11 w-full min-w-11 max-w-md items-center gap-2 rounded-sm border border-line bg-page px-3 text-sm text-fg-muted transition-colors hover:border-line-strong"
        >
          <Search aria-hidden="true" className="size-5 shrink-0" />
          <span className="sr-only sm:not-sr-only sm:truncate">{t('core.nav.search')}</span>
          <kbd className="ml-auto hidden rounded-sm border border-line bg-surface px-1.5 py-0.5 text-xs text-fg-muted sm:inline">
            ⌘K
          </kbd>
        </button>

        <div className="flex shrink-0 items-center gap-0.5">
          {!isDrawer ? (
            <>
              {languageSlot ? <div className="hidden lg:block">{languageSlot}</div> : null}
              {preferenceControls}
            </>
          ) : null}

          <IconButton
            label={appearance === 'light' ? t('core.preferences.darkTheme') : t('core.preferences.lightTheme')}
            onClick={toggleAppearance}
          >
            {appearance === 'light' ? <Moon /> : <Sun />}
          </IconButton>

          <IconButton label={t('core.nav.activityCount', { count: ACTIVITY_COUNT })}>
            <span className="relative">
              <Bolt />
              <span
                aria-hidden="true"
                className="absolute -right-1.5 -top-1.5 inline-flex min-w-4 items-center justify-center rounded-lg bg-danger px-1 text-[0.625rem] font-semibold text-danger-fg"
              >
                {ACTIVITY_COUNT}
              </span>
            </span>
          </IconButton>

          {account ? (
            <AccountDisclosure account={account} />
          ) : (
            <IconButton label={t('core.preferences.open')}>
              <User />
            </IconButton>
          )}
        </div>
      </header>

      {showStrip ? (
        <div className="flex min-h-11 shrink-0 flex-wrap items-center gap-x-2 border-b border-line bg-surface px-3">
          {tenantSlot ? <div className="min-w-0 flex-1">{tenantSlot}</div> : null}
          {navigationInert ? <div className="ml-auto flex items-center gap-0.5">{preferenceControls}</div> : null}
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        {!isDrawer ? (
          <aside
            inert={navigationInert}
            data-inert={navigationInert}
            className="shrink-0 data-[inert=true]:opacity-60"
            style={{ width: isCollapsed ? 'var(--vo-rail-collapsed-width)' : 'var(--vo-rail-width)' }}
          >
            <PrimaryNav
              current={current}
              destinations={destinations}
              railDestinations={railDestinations}
              collapsed={isCollapsed}
              onNavigate={handleNavigate}
              onOpenRoute={handleOpenRoute}
              hasFeature={hasFeature}
            />
          </aside>
        ) : null}

        {/* The only scroll container, so it takes focus: a page with nothing focusable can still be scrolled by keyboard. */}
        <main
          tabIndex={0}
          className="min-w-0 flex-1 overflow-auto focus-visible:-outline-offset-2"
          style={{
            padding: 'var(--vo-content-gutter)',
            // Keeps a focused field from scrolling under a docked save bar.
            scrollPaddingBottom: 'var(--vo-save-bar-offset, 0px)',
          }}
        >
          {children}
        </main>
      </div>

      {/* Mobile drawer — absent from the accessibility tree while closed. */}
      {isDrawer && drawerOpen ? (
        <NavigationDrawer onClose={() => setDrawerOpen(false)}>
          <div className="flex flex-col gap-3 border-b border-line p-3">
            {languageSlot}
            <div className="flex flex-wrap items-center gap-0.5">{preferenceControls}</div>
          </div>
          <PrimaryNav
            current={current}
            destinations={destinations}
            railDestinations={railDestinations}
            onNavigate={handleNavigate}
            onOpenRoute={handleOpenRoute}
            hasFeature={hasFeature}
          />
        </NavigationDrawer>
      ) : null}

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNavigate={handleNavigate}
        onOpenRoute={handleOpenRoute}
        destinations={destinations}
        routes={paletteRoutes}
        features={paletteFeatures}
      />
    </div>
  )
}

/** Demonstration count until Activity has a source. */
const ACTIVITY_COUNT = 3

/*
 * The drawer is a native modal <dialog>: focus moves to Close, stays inside
 * while open, and returns to Menu when it closes. Esc, Close, the backdrop and
 * choosing a destination close it. Its content scrolls as one column, so it
 * keeps every control at 400% zoom.
 */
function NavigationDrawer({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  const t = useT()
  const ref = React.useRef<HTMLDialogElement>(null)
  const closeRef = React.useRef<HTMLButtonElement>(null)

  // A layout effect, so the dialog is closed while still in the document: until
  // it closes, the page behind is inert and the opener cannot take focus.
  React.useLayoutEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const dialog = ref.current
    if (dialog && !dialog.open) dialog.showModal()
    closeRef.current?.focus()
    return () => {
      if (dialog?.open) dialog.close()
      if (opener?.isConnected) opener.focus()
    }
  }, [])

  return (
    <dialog
      ref={ref}
      aria-label={t('core.nav.primaryNavigation')}
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault()
          onClose()
        }
      }}
      onClick={(event) => {
        if (event.target === ref.current) onClose()
      }}
      className="m-0 h-dvh max-h-dvh max-w-[100vw] border-0 bg-surface p-0 text-fg shadow-e3 backdrop:bg-[rgb(8_19_14_/_45%)]"
      style={{ width: 'var(--vo-mobile-drawer-width)' }}
    >
      <div className="flex h-full flex-col">
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-line px-3">
          <ProductMark />
          <IconButton ref={closeRef} label={t('core.nav.closeNavigation')} onClick={onClose}>
            <X />
          </IconButton>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    </dialog>
  )
}

/*
 * Account: a disclosure, not a menu. The trigger reports whether the panel is
 * open. Esc, a click outside, or the trigger closes it and focus returns to
 * the trigger.
 */
function AccountDisclosure({ account }: { account: ShellAccount }) {
  const t = useT()
  const [open, setOpen] = React.useState(false)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const panelId = React.useId()

  const close = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  return (
    <div
      className="relative"
      onKeyDown={(event) => {
        if (open && event.key === 'Escape') {
          event.preventDefault()
          close()
        }
      }}
    >
      <IconButton
        ref={triggerRef}
        label={t('core.preferences.open')}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
      >
        <User />
      </IconButton>

      {open ? (
        <>
          <div className="fixed inset-0 z-40" aria-hidden="true" onClick={close} />
          <div
            id={panelId}
            role="region"
            aria-label={t('core.auth.account')}
            className="absolute right-0 top-full z-50 mt-1 w-[min(16rem,calc(100vw-1.5rem))] rounded-md border border-line bg-surface p-1 shadow-e3"
          >
            <div className="px-3 py-2">
              <p className="text-xs text-fg-muted">{t('core.auth.signedInAs')}</p>
              <p className="text-sm font-medium text-fg-strong wrap-anywhere">
                {account.name ?? account.email ?? t('core.auth.account')}
              </p>
              {account.name && account.email ? (
                <p className="text-xs text-fg-muted wrap-anywhere">{account.email}</p>
              ) : null}
            </div>
            <div className="my-1 h-px bg-line" aria-hidden="true" />
            <button
              type="button"
              onClick={() => {
                // The panel is about to go; a confirmation returns focus to the trigger.
                close()
                account.onLogout()
              }}
              className="flex min-h-10 w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm text-fg transition-colors hover:bg-surface-hover focus-visible:-outline-offset-2"
            >
              <LogOut aria-hidden="true" className="size-4" />
              {t('core.auth.logout')}
            </button>
          </div>
        </>
      ) : null}
    </div>
  )
}
